import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { db } from '../../services/firebase';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, addDoc } from 'firebase/firestore';

const AdminMessages = () => {
  const { currentUser } = useAuth();
  const { createEstimateNotification } = useNotifications();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showConversationList, setShowConversationList] = useState(true);
  const [showEstimateDetails, setShowEstimateDetails] = useState(true);
  const messagesEndRef = useRef(null);
  const unsubscribeRef = useRef(null);
  
  // Message state
  const [message, setMessage] = useState('');

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setShowConversationList(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load all conversations (estimates)
  useEffect(() => {
    console.log('💬 Admin loading all conversations...');
    setLoading(true);

    const q = query(
      collection(db, 'estimates'),
      orderBy('updatedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('💬 Admin messages snapshot received, size:', snapshot.size);
      const estimatesData = snapshot.docs.map(doc => {
        const data = doc.data();
        console.log('💬 Processing admin estimate:', doc.id, 'userId:', data.userId, 'messages:', data.messages?.length || 0);
        return {
          id: doc.id,
          ...data
        };
      });
      
      console.log('💬 Admin conversations set:', estimatesData.length);
      setConversations(estimatesData);
      setLoading(false);

      // Auto-select first conversation if none selected
      if (!selectedConversation && estimatesData.length > 0) {
        console.log('💬 Auto-selecting first admin conversation:', estimatesData[0].id);
        setSelectedConversation(estimatesData[0]);
      }
    }, (error) => {
      console.error('❌ Error loading admin conversations:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Setup real-time listener for selected conversation
  useEffect(() => {
    if (!selectedConversation) return;

    // Clean up previous listener
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }

    const estimateRef = doc(db, 'estimates', selectedConversation.id);
    const unsubscribe = onSnapshot(estimateRef, (doc) => {
      if (doc.exists()) {
        const updatedEstimate = { id: doc.id, ...doc.data() };
        setSelectedConversation(updatedEstimate);
        
        // Update in conversations list
        setConversations(prev => 
          prev.map(conv => 
            conv.id === updatedEstimate.id ? updatedEstimate : conv
          )
        );

        // Mark messages as read (admin has seen them)
        markMessagesAsRead(updatedEstimate);
      }
    });

    unsubscribeRef.current = unsubscribe;

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [selectedConversation?.id]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [selectedConversation?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const markMessagesAsRead = async (conversation) => {
    if (!conversation.hasUnreadMessages) return;

    try {
      await updateDoc(doc(db, 'estimates', conversation.id), {
        hasUnreadMessages: false
      });
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  // Create initial messages from estimate data if none exist
  const createInitialMessagesFromEstimate = async (estimate) => {
    if (estimate.messages && estimate.messages.length > 0) {
      return estimate; // Already has messages
    }

    const initialMessages = [];
    
    // Add description as initial message
    if (estimate.description && estimate.description.trim()) {
      initialMessages.push({
        id: Date.now().toString(),
        text: estimate.description.trim(),
        sender: 'client',
        senderName: estimate.userName || estimate.userEmail?.split('@')[0] || 'Client',
        senderEmail: estimate.userEmail,
        timestamp: estimate.createdAt || new Date(),
        read: false,
        isInitialRequest: true
      });
    }

    // Add files as separate messages
    if (estimate.files && estimate.files.length > 0) {
      estimate.files.forEach((file, index) => {
        initialMessages.push({
          id: (Date.now() + index + 1).toString(),
          text: '', // No caption for initial files
          sender: 'client',
          senderName: estimate.userName || estimate.userEmail?.split('@')[0] || 'Client',
          senderEmail: estimate.userEmail,
          timestamp: estimate.createdAt || new Date(),
          read: false,
          fileUrl: file.url,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          isInitialRequest: true
        });
      });
    }

    // Update the estimate with initial messages if any were created
    if (initialMessages.length > 0) {
      try {
        await updateDoc(doc(db, 'estimates', estimate.id), {
          messages: initialMessages,
          hasUnreadMessages: true,
          updatedAt: new Date()
        });

        // Return updated estimate
        return {
          ...estimate,
          messages: initialMessages,
          hasUnreadMessages: true
        };
      } catch (error) {
        console.error('Error creating initial messages:', error);
        return estimate;
      }
    }

    return estimate;
  };

  const handleSelectConversation = async (conversation) => {
    // Create initial messages if they don't exist
    const updatedConversation = await createInitialMessagesFromEstimate(conversation);
    setSelectedConversation(updatedConversation);
    
    if (isMobile) {
      setShowConversationList(false);
    }
  };

  const handleBackToList = () => {
    setShowConversationList(true);
    setSelectedConversation(null);
  };

  const handleSendMessage = async (messageText) => {
    if (!selectedConversation || !messageText.trim() || sendingMessage) return;
    
    setSendingMessage(true);
    try {
      // Create admin message object
      const adminMessage = {
        id: Date.now().toString(),
        text: messageText.trim(),
        sender: 'admin',
        senderName: currentUser.displayName || currentUser.email.split('@')[0] || 'Admin',
        senderEmail: currentUser.email,
        timestamp: new Date(),
        read: true
      };
      
      // Save message to the database
      const currentMessages = selectedConversation.messages || [];
      const updatedMessages = [...currentMessages, adminMessage];
      
      await updateDoc(doc(db, 'estimates', selectedConversation.id), {
        messages: updatedMessages,
        updatedAt: new Date(),
        hasUnreadMessages: false // Admin sent message, so no unread for admin
      });
      
      showNotification('Message sent successfully!', 'success');
      
    } catch (error) {
      console.error('Error sending message:', error);
      showNotification('Failed to send message. Please try again.', 'error');
    } finally {
      setSendingMessage(false);
    }
  };

  // No additional message handling functions needed - all handled by AIAssistant component

  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Mobile layout: show either conversation list or chat
  if (isMobile) {
    return (
      <Layout>
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
          {/* Mobile Header */}
          <div style={{
            background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
            color: 'white',
            padding: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            {!showConversationList && (
              <button
                onClick={handleBackToList}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  padding: '0.5rem'
                }}
              >
                ←
              </button>
            )}
            <h1 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '600' }}>
              {showConversationList ? 'Client Messages' : selectedConversation?.userName || 'Chat'}
            </h1>
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflow: 'hidden' }}>
            {showConversationList ? (
              <ConversationList
                conversations={conversations}
                selectedConversation={selectedConversation}
                onSelectConversation={handleSelectConversation}
                currentUserType="admin"
                loading={loading}
              />
            ) : (
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Messages Display */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', padding: '1rem', overflowY: 'auto' }}>
                  {selectedConversation?.messages?.map((message, index) => (
                    <div 
                      key={message.id || index}
                      style={{
                        display: 'flex',
                        justifyContent: message.sender === 'admin' ? 'flex-end' : 'flex-start',
                        marginBottom: '0.75rem'
                      }}
                    >
                      <div
                        style={{
                          maxWidth: '70%',
                          padding: '0.875rem 1rem',
                          borderRadius: message.sender === 'admin' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          backgroundColor: message.sender === 'admin' ? '#007bff' : '#f8f9fa',
                          color: message.sender === 'admin' ? 'white' : '#333',
                          border: message.sender !== 'admin' ? '1px solid #e0e0e0' : 'none',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
                        }}
                      >
                        <div style={{ fontSize: '0.85rem', lineHeight: '1.5', marginBottom: '0.375rem' }}>
                          {message.text}
                        </div>
                        <div style={{
                          fontSize: '0.7rem',
                          opacity: 0.75,
                          textAlign: message.sender === 'admin' ? 'right' : 'left',
                          marginTop: '0.375rem'
                        }}>
                          {message.sender === 'admin' ? 'You' : message.senderName || 'Client'} • {
                            new Date(message.timestamp?.toDate ? message.timestamp.toDate() : message.timestamp).toLocaleString()
                          }
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                
                {/* Message Input */}
                <div style={{ padding: '1rem', borderTop: '1px solid #e0e0e0', background: 'white' }}>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <input 
                      type="text" 
                      placeholder="Type your message to the client..."
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        outline: 'none'
                      }}
                      value={message || ''}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage(message);
                          setMessage('');
                        }
                      }}
                      disabled={sendingMessage}
                    />
                    <button
                      onClick={() => {
                        handleSendMessage(message);
                        setMessage('');
                      }}
                      disabled={!message?.trim() || sendingMessage}
                      style={{
                        background: (!message?.trim() || sendingMessage) ? '#ccc' : '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '0 1rem',
                        cursor: (!message?.trim() || sendingMessage) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {sendingMessage ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div style={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            background: notification.type === 'success' ? '#d4edda' : '#f8d7da',
            color: notification.type === 'success' ? '#155724' : '#721c24',
            padding: '1rem',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            maxWidth: '300px'
          }}>
            {notification.message}
          </div>
        )}
      </Layout>
    );
  }

  // Desktop layout: three-column layout
  return (
    <Layout>
      <div style={{ 
        height: 'calc(100vh - 80px)', // Subtract header height
        display: 'flex', 
        flexDirection: 'column',
        maxWidth: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
          color: 'white',
          padding: isMobile ? '0.75rem' : '1rem 1.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          flexShrink: 0
        }}>
          <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>Client Messages</h1>
          <p style={{ margin: '0.25rem 0 0 0', opacity: 0.9, fontSize: '0.8rem' }}>
            Manage conversations with clients about their estimates
          </p>
        </div>

        {/* Main Content */}
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          overflow: 'hidden',
          minHeight: 0 // Important for flexbox overflow
        }}>
          {/* Conversation List */}
          <div style={{
            width: isMobile ? '100%' : '350px',
            minWidth: isMobile ? '100%' : '300px',
            borderRight: isMobile ? 'none' : '1px solid #e0e0e0',
            background: 'white',
            display: showConversationList || !isMobile ? 'flex' : 'none',
            flexDirection: 'column'
          }}>
            <div style={{
              padding: '1rem',
              borderBottom: '1px solid #f0f0f0',
              background: '#f8f9fa'
            }}>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#333' }}>
                Client Conversations ({conversations.length})
              </h3>
            </div>
            
            <div style={{ 
              flex: 1, 
              overflow: 'auto',
              minHeight: 0 // Important for flexbox overflow
            }}>
              <ConversationList
                conversations={conversations}
                selectedConversation={selectedConversation}
                onSelectConversation={handleSelectConversation}
                currentUserType="admin"
                loading={loading}
              />
            </div>
          </div>

          {/* Chat Area */}
          <div style={{ 
            flex: 1, 
            display: (showConversationList || !isMobile) ? 'flex' : 'none',
            flexDirection: 'column', 
            background: '#f8f9fa',
            minHeight: 0, // Important for flexbox overflow
            maxHeight: 'calc(100vh - 80px)', // Asegurar altura máxima
            overflow: 'hidden' // Prevenir overflow del contenedor padre
          }}>
            {selectedConversation ? (
              <>

                {/* Estimate Details - Compact Version */}
                <div style={{
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                  padding: isMobile ? '0.75rem' : '1rem 1.5rem',
                  borderBottom: '1px solid #dee2e6',
                  fontSize: '0.8rem',
                  flexShrink: 0
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '0.5rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <span style={{ color: '#6c757d', fontWeight: '500' }}>Service:</span>
                        <span style={{ color: '#495057', fontWeight: '600' }}>{selectedConversation.service}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <span style={{ color: '#6c757d', fontWeight: '500' }}>Submitted:</span>
                        <span style={{ color: '#495057' }}>{new Date(selectedConversation.submittedDate).toLocaleDateString()}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <span style={{ color: '#6c757d', fontWeight: '500' }}>Email:</span>
                        <span style={{ color: '#495057' }}>{selectedConversation.userEmail}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowEstimateDetails(!showEstimateDetails)}
                      style={{
                        background: 'transparent',
                        border: '1px solid #dee2e6',
                        borderRadius: '6px',
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                        color: '#6c757d',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#e9ecef';
                        e.target.style.color = '#495057';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent';
                        e.target.style.color = '#6c757d';
                      }}
                    >
                      {showEstimateDetails ? 'Hide Details' : 'Show Details'}
                    </button>
                  </div>
                  
                  {/* Expandable Description */}
                  {showEstimateDetails && (
                    <div style={{ 
                      marginTop: '0.75rem',
                      padding: '0.75rem',
                      background: 'white',
                      borderRadius: '8px',
                      border: '1px solid #dee2e6'
                    }}>
                      <div style={{ 
                        fontSize: '0.75rem', 
                        color: '#6c757d', 
                        fontWeight: '500', 
                        marginBottom: '0.5rem' 
                      }}>
                        Description:
                      </div>
                      <div style={{ 
                        fontSize: '0.8rem',
                        color: '#495057',
                        lineHeight: '1.4',
                        maxHeight: '80px',
                        overflowY: 'auto'
                      }}>
                        {selectedConversation.description}
                      </div>
                    </div>
                  )}
                </div>

                {/* Messages Area */}
                <div style={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f1f3f4 100%)',
                  minHeight: 0,
                  height: isMobile ? 'calc(100vh - 400px)' : 'calc(100vh - 500px)',
                  position: 'relative',
                  WebkitOverflowScrolling: 'touch',
                  border: '1px solid #e1e5e9',
                  borderRadius: '8px',
                  margin: '0.5rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {/* Header */}
                  <div style={{
                    background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                    color: 'white',
                    padding: '1rem 1.25rem',
                    borderRadius: '8px 8px 0 0',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '0.5rem'
                  }}>
                    <div>
                      <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '1.1rem', fontWeight: '600' }}>
                        Client Conversation
                      </h3>
                      <p style={{ margin: 0, fontSize: '0.8rem', opacity: 0.9 }}>
                        Communicate directly with your client about their estimate
                      </p>
                    </div>
                  </div>
                  
                  {/* Messages Display */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', padding: '1rem', overflowY: 'auto' }}>
                    {selectedConversation?.messages?.map((message, index) => (
                      <div 
                        key={message.id || index}
                        style={{
                          display: 'flex',
                          justifyContent: message.sender === 'admin' ? 'flex-end' : 'flex-start',
                          marginBottom: '0.75rem'
                        }}
                      >
                        <div
                          style={{
                            maxWidth: '70%',
                            padding: '0.875rem 1rem',
                            borderRadius: message.sender === 'admin' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                            backgroundColor: message.sender === 'admin' ? '#007bff' : '#f8f9fa',
                            color: message.sender === 'admin' ? 'white' : '#333',
                            border: message.sender !== 'admin' ? '1px solid #e0e0e0' : 'none',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
                          }}
                        >
                          <div style={{ fontSize: '0.85rem', lineHeight: '1.5', marginBottom: '0.375rem' }}>
                            {message.text}
                          </div>
                          <div style={{
                            fontSize: '0.7rem',
                            opacity: 0.75,
                            textAlign: message.sender === 'admin' ? 'right' : 'left',
                            marginTop: '0.375rem'
                          }}>
                            {message.sender === 'admin' ? 'You' : message.senderName || 'Client'} • {
                              new Date(message.timestamp?.toDate ? message.timestamp.toDate() : message.timestamp).toLocaleString()
                            }
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                  
                  {/* Message Input */}
                  <div style={{ padding: '1rem', borderTop: '1px solid #e0e0e0', background: 'white' }}>
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <input 
                        type="text" 
                        placeholder="Type your message to the client..."
                        style={{
                          flex: 1,
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          fontSize: '0.875rem',
                          outline: 'none'
                        }}
                        value={message || ''}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage(message);
                            setMessage('');
                          }
                        }}
                        disabled={sendingMessage}
                      />
                      <button
                        onClick={() => {
                          handleSendMessage(message);
                          setMessage('');
                        }}
                        disabled={!message?.trim() || sendingMessage}
                        style={{
                          background: (!message?.trim() || sendingMessage) ? '#ccc' : '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '0 1rem',
                          cursor: (!message?.trim() || sendingMessage) ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {sendingMessage ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                  </div>
                </div>

              </>
            ) : (
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                color: '#666',
                padding: '2rem'
              }}>
                <div style={{ fontSize: '5rem', marginBottom: '1rem' }}>💬</div>
                <h3 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>Select a conversation</h3>
                <p style={{ margin: 0, textAlign: 'center' }}>
                  Choose a client conversation from the left to start chatting
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          background: notification.type === 'success' ? '#d4edda' : '#f8d7da',
          color: notification.type === 'success' ? '#155724' : '#721c24',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          maxWidth: '400px'
        }}>
          {notification.message}
        </div>
      )}
    </Layout>
  );
};

// Helper function to get status color
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'pending': return '#ffc107';
    case 'quoted': return '#17a2b8';
    case 'approved': return '#28a745';
    case 'declined': return '#dc3545';
    case 'completed': return '#6f42c1';
    default: return '#6c757d';
  }
};

export default AdminMessages;
